<script lang="ts" setup>
  import { router } from '@/routes/router.ts';
  import { PlusIcon } from '@heroicons/vue/20/solid';
  import dayjs from 'dayjs';
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useApi } from '@/composables/use-api.ts';
  import { ApiFlexError } from '@/services/api/api-error.ts';
  import { showBackEndFlexErrorMessage } from '@/utils/error-message-helper.ts';
  import {
    AttendanceAction,
    AttendanceActivityDto,
    AttendanceLocation,
  } from '@/services/api/attendance-activity-api.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { LocationTypes } from '@/utils/item-printer.ts';

  const actionTypes = [
    { text: '근무 시작', value: 'WORK_START' },
    { text: '근무 종료', value: 'WORK_END' },
    { text: '자리비움 시작', value: 'AWAY_START' },
    { text: '자리비움 종료', value: 'AWAY_END' },
  ] as const;

  const props = defineProps<{
    attendanceId: string;
    targetDate: string;
  }>();

  const { attendance } = useApi();
  const activities = ref<AttendanceActivityDto[]>([]);
  const loading = ref<boolean>(false);
	
	const alert = reactive<{
		visible: boolean;
		message: string;
	}>({
		visible: false,
		message: ''
	});

  const formAdd = reactive<{
    visible: boolean;
    attendanceId: number;
    action: string;
    location?: AttendanceLocation;
    recordDt: string;
    comment?: string;
  }>({
    visible: false,
    attendanceId: Number(props.attendanceId),
    action: 'WORK_START',
    location: 'INSIDE',
    recordDt: props.targetDate + ' ' + dayjs().format('HH:mm'),
    comment: undefined,
  });
  const isValidFormAdd = computed(() => {
    return formAdd.action && formAdd.recordDt;
  });

  const formUpdate = reactive<{
    visible: boolean;
    id: number;
    attendanceId: number;
    action: string;
    location?: AttendanceLocation;
    orgRecordDt: string;
    recordDt: string;
    comment?: string;
  }>({
    visible: false,
    id: 0,
    attendanceId: Number(props.attendanceId),
    action: '',
    location: undefined,
    recordDt: '',
    orgRecordDt: '',
    comment: undefined,
  });
  const isValidFormUpdate = computed(() => {
    return formUpdate.action && formUpdate.recordDt;
  });
	
	function resetAlert() {
		alert.visible = false;
		alert.message = '';
	}

  function resetAddActivityInputs() {
    formAdd.action = 'WORK_START';
    formAdd.location = 'INSIDE';
    formAdd.recordDt = props.targetDate + ' ' + dayjs().format('HH:mm');
    formAdd.comment = undefined;
  }

  async function refreshAsync() {
    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          loading.value = status;
        },
        async () => {
          activities.value = await attendance.activity.listByAttendanceIdAsync(
            Number(props.attendanceId)
          );
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '근태 활동 정보 조회 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
  }

  async function addActivity() {
    console.log('addActivity:', formAdd);

    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          loading.value = status;
        },
        async () => {
          await attendance.activity.createAsync({
            attendanceId: formAdd.attendanceId,
            action: formAdd.action as AttendanceAction,
            recordDt: dayjs(formAdd.recordDt).format('YYYY-MM-DDTHH:mm:00'),
            location: formAdd.location ?? 'INSIDE',
            comment: formAdd.comment ?? '',
          });
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '근태 활동 추가 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
    formAdd.visible = false;
    resetAddActivityInputs();
    await refreshAsync();
  }

  // for activity update emit event
  function handleUpdateActivity(activityId: number) {
    console.log('handleUpdateActivity:', activityId);
    const activity = activities.value?.find((item) => item.id == activityId);
    if (activity) {
      formUpdate.id = activity.id;
      formUpdate.attendanceId = activity.attendanceId;
      formUpdate.action = activity.action;
      formUpdate.recordDt = dayjs(activity.recordDt).format('YYYY-MM-DD HH:mm');
      formUpdate.orgRecordDt = activity.recordDt;
      formUpdate.location = activity.location ?? 'INSIDE';
      formUpdate.comment = activity.comment ?? '';
    }

    formUpdate.visible = true;
  }

  async function updateActivity() {
    console.log('updateActivity:', formUpdate);
    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          loading.value = status;
        },
        async () => {
          await attendance.activity.modifyAsync({
            id: formUpdate.id,
            action: formUpdate.action as AttendanceAction,
            recordDt: dayjs(formUpdate.recordDt).format('YYYY-MM-DDTHH:mm:00'),
            location: formUpdate.location ?? 'INSIDE',
            comment: formUpdate.comment ?? '',
          });
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '근태 활동 수정 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
    formUpdate.visible = false;
    await refreshAsync();
  }

  // for activity delete emit event
  async function deleteAsync(activityId: number) {
    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          loading.value = status;
        },
        async () => {
          await attendance.activity.removeAsync(activityId);
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '근태 활동 삭제 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
    await refreshAsync();
  }

  function cancelAdd() {
    resetAddActivityInputs();
    formAdd.visible = false;
  }
	
	function initFormFromQuery() {
		const { id, recordDt, direction } = router.currentRoute.value.query;
		
		const activityId = Number(id);
		const recordDateTime = recordDt as string;
		const direct = direction as string;
		
		if(activityId)
			handleUpdateActivity(activityId);
		if(recordDateTime) {
			handleAddActivity(direct, recordDateTime);
		}
	}
	
	function handleAddActivity(direct?: string, recordDateTime?: string) {
		formAdd.recordDt = recordDateTime
			? dayjs(recordDateTime).format('YYYY-MM-DDTHH:mm')
			: props.targetDate + ' ' + dayjs().format('HH:mm');
		
		const activityBeforeRecordDt = activities.value?.filter(item => !recordDateTime ? true : dayjs(item.recordDt) <= dayjs(recordDateTime) ).at(-1);
		
		// recordDt 이전의 activity로 validation
		if(direct) {
			if(direct == 'IN') {
				if(!activityBeforeRecordDt || activityBeforeRecordDt?.action == 'WORK_END')
					formAdd.action = 'WORK_START';
				else if(activityBeforeRecordDt?.action == 'AWAY_START')
					formAdd.action = 'AWAY_END';
				else if(activityBeforeRecordDt?.action == 'WORK_START') {
					openAlertWithMsg(`이미 근무가 시작된 상태입니다. (${dayjs(activityBeforeRecordDt.recordDt).format('HH:mm')})\n 근무 시작 시간을 수정해 주세요.`);
					formAdd.visible = false;
					return;
				}
			}else {
				if(!activityBeforeRecordDt) {
					openAlertWithMsg('근무를 시작하지 않았습니다. \n먼저 [근무 시작] 버튼을 클릭해 주세요.');
					formAdd.visible = false;
					return;
				}else if(activityBeforeRecordDt.action == 'WORK_START' || activityBeforeRecordDt.action == 'AWAY_END' || activityBeforeRecordDt.action == 'AWAY_START') {
					formAdd.action = 'WORK_END';
				}else if(activityBeforeRecordDt.action == 'WORK_END') {
					openAlertWithMsg(`이미 근무가 종료된 상태입니다. (${dayjs(activityBeforeRecordDt.recordDt).format('HH:mm')})\n 근무 종료 시간을 수정해 주세요.`);
					formAdd.visible = false;
					return;
				}
			}
		}else {
			if(activityBeforeRecordDt?.action == 'WORK_START' || activityBeforeRecordDt?.action == 'AWAY_END') {
				formAdd.action = 'WORK_END';
			} else if(activityBeforeRecordDt?.action == 'AWAY_START') {
				formAdd.action = 'AWAY_END';
			}
		}
		
		const activityAfterRecordDt = activities.value?.filter(item => dayjs(item.recordDt) > dayjs(recordDateTime)).at(0);
		
		// recordDt 이후의 activity로 validation
		if(recordDateTime) {
			if (activities.value?.find((item) => dayjs(item.recordDt) == dayjs(recordDateTime))) {
				openAlertWithMsg('동일한 시간에 등록된 근무가 있습니다.');
				return;
			}
			if(activityAfterRecordDt) {
				if (direct == 'IN' && activityAfterRecordDt.action == 'WORK_START') {
					openAlertWithMsg(`이미 근무가 시작된 상태입니다. (${dayjs(activityAfterRecordDt.recordDt).format('HH:mm')})\n 근무 시작 시간을 수정해 주세요.`);
					return;
				}
				if (direct == 'OUT' && activityAfterRecordDt.action == 'WORK_END') {
					openAlertWithMsg(`이미 근무가 종료된 상태입니다. (${dayjs(activityAfterRecordDt.recordDt).format('HH:mm')})\n 근무 시작을 등록해 주세요.`);
					return;
				}
			}
		}
		
		formAdd.visible = true;
	}
	
	function openAlertWithMsg(message?: string) {
		alert.visible = true;
		
		if(message)
			alert.message = message;
	}
	
	function closeAlert() {
		resetAlert();
	}

  onMounted(async () => {
    console.log('onMounted :' + router.currentRoute.value.name?.toString());
    await refreshAsync();
	  initFormFromQuery();
    console.log('reloadActivities called');
  });
</script>

<template>
  <DefaultLayout
    :title="dayjs(props.targetDate).format('YYYY년 M월 D일') + ' 상세'"
    go-back>
    <skeleton
      v-if="loading"
      class="my-5 mb-8" />
    <div
      v-else
      id="activity_list"
      class="pb-5">
      <div v-if="activities && activities.length > 0">
        <Activity
          v-for="item in activities"
          :id="item.id || 0"
          :key="item.id"
          :action="item.action"
          :attendance-id="item.attendanceId"
          :record-dt="item.recordDt"
          @update-activity="handleUpdateActivity"
          @delete-activity="deleteAsync" />
      </div>

      <div
        v-else
        class="alert my-4">
        데이터가 없습니다!
      </div>

      <div class="pb-2 text-center">
        <button
          class="text-base btn btn-circle btn-primary"
          @click="handleAddActivity()">
          <PlusIcon class="w-5 h-5" />
        </button>
      </div>
    </div>
  </DefaultLayout>

  <!-- 수정 -->
  <AppModal
    id="update_activity"
    has-close-x
    :is-visible="formUpdate.visible"
    @close="formUpdate.visible = false">
    <FormField
      class="pt-5"
      title="근태 활동 유형">
      <select
        v-model="formUpdate.action"
        disabled
        class="w-full select select-bordered">
        <option
          disabled
          selected>
          근태 활동 유형
        </option>
        <option
          v-for="type in actionTypes"
          :key="type.value"
          :value="type.value">
          {{ type.text }}
        </option>
      </select>
    </FormField>
    <FormField title="기존 시간 값">
      <input
        :value="dayjs(formUpdate.orgRecordDt).format('YYYY-MM-DD HH:mm')"
        class="w-full input input-bordered"
        disabled
        type="datetime-local" />
    </FormField>

    <FormField
      v-if="
        formUpdate.action == 'WORK_START' || formUpdate.action == 'AWAY_END'
      "
      title="근무 위치">
      <select
        v-model="formUpdate.location"
        class="w-full select select-bordered">
        <option
          disabled
          selected>
          근무 위치
        </option>
        <option
          v-for="type in LocationTypes"
          :key="type.value"
          :value="type.value">
          {{ type.text }}
        </option>
      </select>
    </FormField>
    <FormField title="근무 일시">
      <input
        v-model="formUpdate.recordDt"
        class="w-full input input-bordered"
        type="datetime-local" />
    </FormField>

    <template v-if="formUpdate.action == 'WORK_END'">
      <FormField title="근무 코멘트 (선택)">
        <textarea
          v-model="formUpdate.comment"
          class="w-full textarea textarea-bordered" />
      </FormField>
    </template>
    <div class="flex items-center justify-center mt-6 mb-2 join">
      <button
        class="w-32 join-item btn"
        :disabled="loading"
        @click="formUpdate.visible = false">
        취소
      </button>
      <button
        class="w-32 join-item btn btn-primary"
        :disabled="!isValidFormUpdate || loading"
        @click="updateActivity">
        <span
          v-if="loading"
          class="loading loading-spinner" />
        수정
      </button>
    </div>
  </AppModal>

  <!-- 추가 -->
  <AppModal
    id="add_activity"
    has-close-x
    :is-visible="formAdd.visible"
    @close="cancelAdd">
    <FormField
      class="pt-5"
      title="근태 활동 유형">
      <select
        v-model="formAdd.action"
        class="w-full select select-bordered">
        <option
          disabled
          selected>
          근태 활동 유형
        </option>
        <option
          v-for="type in actionTypes"
          :key="type.value"
          :value="type.value">
          {{ type.text }}
        </option>
      </select>
    </FormField>

    <FormField
      v-if="formAdd.action == 'WORK_START' || formAdd.action == 'AWAY_END'"
      title="근무 위치">
      <select
        v-model="formAdd.location"
        class="w-full select select-bordered">
        <option
          disabled
          selected>
          근무 위치
        </option>
        <option
          v-for="type in LocationTypes"
          :key="type.value"
          :value="type.value">
          {{ type.text }}
        </option>
      </select>
    </FormField>

    <FormField title="근무 일시">
      <input
        v-model="formAdd.recordDt"
        class="w-full input input-bordered"
        type="datetime-local" />
    </FormField>

    <template v-if="formAdd.action == 'WORK_END'">
      <FormField title="근무 코멘트 (선택)">
        <textarea
          v-model="formAdd.comment"
          class="w-full textarea textarea-bordered" />
      </FormField>
    </template>
    <div class="flex items-center justify-center mt-6 mb-2 join">
      <button
        class="w-32 join-item btn"
        :disabled="loading"
        @click="cancelAdd">
        취소
      </button>
      <button
        class="w-32 join-item btn btn-primary"
        :disabled="!isValidFormAdd || loading"
        @click="addActivity">
        <span
          v-if="loading"
          class="loading loading-spinner" />
        추가
      </button>
    </div>
  </AppModal>
	
	<AppModal
		id="alert_display"
		has-close-x
		:is-visible="alert.visible"
		@close="closeAlert"
	>
		<div class="flex-row items-center justify-center">
					<div class="flex items-center justify-center text-xl mb-2 text-red-500 font-bold">
						알림
					</div>
					<div class="flex items-center justify-center font-bold whitespace-pre text-center mt-2 mb-2">
						{{alert.message}}
					</div>
					<div class="flex items-center justify-center mt-6 mb-2 join">
						<button
							class="w-24 join-item btn btn-outline"
							:disabled="loading"
							@click="closeAlert">
							닫기
						</button>
					</div>
		</div>
	</AppModal>
	
</template>
